/* eslint-disable @typescript-eslint/no-unused-vars */
import './OrganisationDetailsPanel.scss'
import { Avatar, Button } from '@fluentui/react-components'
import { Edit24Regular } from '@fluentui/react-icons'
import { useDotnetClient } from '@/contexts/DotnetClientContext'
import { OverlayDrawerComponent } from '../OverlayDrawerComponent/OverlayDrawerComponent'
import { useEffect, useState } from 'react'
import { AddOrganisationForm } from '../Forms/AddOrganisationForm'
import { MessagesSubscriptionsOrganisationSummary } from '@/dotnet-client'
import { useSearchParams } from 'react-router-dom'
import { createRestClient } from '@/contexts/QueryProvider'
import { Loading } from '../Loading'
import { ErrorComponent } from '../ErrorComponent'
import { MaintainPlatformAdmins } from './MaintainPlatformAdmins'
import { useSessionStorage } from '@/hooks/useSessionStorage'
import { isSuperUser, isApplicationAdmin, isPlatformAdmin } from '@/util'

export const OrganisationDetailsPanel = () => {
  const { dmpClient, isDMPClientLoaded } = useDotnetClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [organisation, setOrganisation] = useState<MessagesSubscriptionsOrganisationSummary>()
  const [editOrgOpen, setEditOrgOpen] = useState(false)
  const [editAdminsOpen, setEditAdminsOpen] = useState(false)
  const orgId = parseInt(searchParams.get('orgId') ?? '')

  useEffect(() => {
    const fetchOrganisation = async () => {
      const result = await dmpClient?.subscriptionsApi.getSubscriptionsApiGetOrganisation(orgId)
      setOrganisation(result)
    }

    if (isDMPClientLoaded) fetchOrganisation()
  }, [dmpClient?.subscriptionsApi, isDMPClientLoaded, orgId])

  const generalDetails = [
    { label: 'Name', value: organisation?.name },
    { label: 'Long Name', value: organisation?.longName },
    { label: 'Description', value: organisation?.description },
    { label: 'Status', value: organisation?.active ? 'Active' : 'Inactive' },
    { label: 'Whitelisted domain', value: organisation?.whitelistedDomain },
  ]

  const { getSubscriptionsApiListOrganisationPlatformAdmins } = createRestClient()
  const {
    data: currentAdmins,
    isPending: isCurrentAdminsPending,
    isError: isCurrentAdminsError,
  } = getSubscriptionsApiListOrganisationPlatformAdmins.useQuery(
    { query: { organisationId: orgId } },
    {
      enabled: !!orgId,
    }
  )

  const [userRole] = useSessionStorage('roles', 1)
  const canEditOrg = isSuperUser(userRole) || isPlatformAdmin(userRole)
  const canEditAdmins = isSuperUser(userRole) || isPlatformAdmin(userRole)

  if (isCurrentAdminsPending) return <Loading />
  if (isCurrentAdminsError) return <ErrorComponent />

  return (
    <>
      <div className="flex flex-col gap-y-5 py-4">
        <div className="w-fit space-y-4">
          <div className="flex w-fit flex-col gap-y-4 rounded bg-white p-6 pt-4">
            <div className="flex flex-row items-center justify-between border-b">
              <h6>General</h6>
              <Button
                disabled={!canEditOrg}
                appearance="subtle"
                icon={<Edit24Regular className="icon-button" />}
                onClick={() => setEditOrgOpen(true)}
              />
            </div>
            <div className="flex shrink-0 flex-row flex-wrap gap-x-20 gap-y-4">
              {generalDetails.map(({ label, value }) => (
                <div className="flex flex-col whitespace-nowrap" key={label}>
                  <span className="text-sm font-light">{label}:</span>
                  <span className="text-base">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex w-full flex-col gap-y-4 rounded p-6 pt-4">
          <div className="flex flex-row justify-between border-b">
            <h6>Platform administrators</h6>
            <Button
              disabled={!canEditAdmins}
              appearance="subtle"
              icon={<Edit24Regular className="icon-button" />}
              onClick={() => {
                setEditAdminsOpen(true)
              }}
            />
          </div>
          <div className="flex flex-row flex-wrap gap-x-2 whitespace-nowrap text-base">
            {!currentAdmins.length && <span className="font-light">(None assigned)</span>}
            {currentAdmins.map((a, i) => (
              <span
                key={`${a.givenName}-${a.familyName}`}
              >{`${a.givenName} ${a.familyName}${i < currentAdmins.length - 1 ? ',' : ''}`}</span>
            ))}
          </div>
        </div>
      </div>

      <OverlayDrawerComponent
        open={editOrgOpen}
        setOpen={setEditOrgOpen}
        title="Edit organisation details"
        content={<AddOrganisationForm onSubmit={() => setEditOrgOpen(false)} selectedOrg={organisation} />}
      />

      <OverlayDrawerComponent
        key="edit-admins"
        size="medium"
        open={editAdminsOpen}
        setOpen={setEditAdminsOpen}
        title="Maintain platform admins"
        content={
          <MaintainPlatformAdmins
            selectedOrg={organisation!}
            onSubmit={() => {
              setEditAdminsOpen(false)
            }}
          />
        }
      />
    </>
  )
}
